// Static interface copy in every supported app language. User and model content is not translated here.
import type { NamespaceCatalog } from '@/i18n/ns';

const en = {
  shareTitle: 'Share App',
  unshareTitle: 'Unshare App',
  unshareBody: 'People will lose access. Your App and its data are kept.',
  shareBody: 'Choose who can use this App and who can edit it.',
  cancel: 'Cancel',
  checking: 'Checking and saving…',
  checkAgain: 'Check again',
  share: 'Share',
  unshare: 'Unshare',
  company: 'your company',
  owner: 'the owner',
  stateUnavailable: 'This sharing state is no longer available.',
  checkFailed: 'The App could not pass its automatic safety checks. Sharing has not changed.',
  changed: 'We couldn’t confirm sharing. Check again to reuse the same request.',
  privateReadWarning:
    'This App has results from {sources}. Everyone you share it with can see them, but nobody can use those connections.',
  unnamedConnection: 'a private connection',
  versionsLoading: 'Loading saved versions…',
  versionsFailed: 'Saved versions are unavailable. Reload the App before trying again.',
  version: 'Saved version {n}',
  current: 'Current',
  restore: 'Restore previous version',
  restoring: 'Checking and restoring…',
  restoreBody:
    'This saves the previous code and design as a new version. Sharing, entries and files are kept.',
  restoreFailed: 'We couldn’t confirm the restore. Try again to check the same request.',
  restoreIncompatible:
    'That version needs a separate data or source review. The current version is unchanged.',
  sharedChecking:
    'Shared. Others can open it as soon as its automatic safety check finishes, usually within a minute.',
  preparing: 'Getting {app} ready…',
  preparingUnnamed: 'Getting this App ready…',
  checkBlocked:
    'This version didn’t pass its automatic safety check, so it can’t be opened. Ask in chat to fix it.',
  checkBlockedSecret:
    'This version contains something that looks like a password or key, so it can’t be opened. Ask in chat to remove it.',
  checkBlockedDependency:
    'This version uses a package with a known security problem, so it can’t be opened. Ask in chat to update it.',
  checkUnavailable: 'This version’s safety check couldn’t finish. Try again in a moment.',
};

const copy: NamespaceCatalog<keyof typeof en> = {
  bg: {
    shareTitle: 'Споделяне на приложение',
    unshareTitle: 'Спиране на споделянето на приложение',
    unshareBody: 'Хората ще загубят достъп. Вашето приложение и данните му ще бъдат запазени.',
    shareBody: 'Изберете кой може да използва това приложение и кой може да го редактира.',
    cancel: 'Отказ',
    checking: 'Проверка и запазване…',
    checkAgain: 'Проверете отново',
    share: 'Споделяне',
    unshare: 'Спиране на споделянето',
    company: 'вашата компания',
    owner: 'собственика',
    stateUnavailable: 'Това състояние на споделяне вече не е налично.',
    checkFailed:
      'Приложението не премина автоматичните проверки за безопасност. Споделянето не е променено.',
    changed:
      'Не успяхме да потвърдим споделянето. Проверете отново, за да използвате същата заявка.',
    privateReadWarning:
      'Това приложение има резултати от {sources}. Всеки, с когото го споделите, може да ги вижда, но никой не може да използва тези връзки.',
    unnamedConnection: 'частна връзка',
    versionsLoading: 'Зареждане на запазените версии…',
    versionsFailed:
      'Запазените версии не са налични. Презаредете приложението, преди да опитате отново.',
    version: 'Запазена версия {n}',
    current: 'Текуща',
    restore: 'Възстановяване на предишна версия',
    restoring: 'Проверка и възстановяване…',
    restoreBody:
      'Това запазва предишния код и дизайн като нова версия. Споделянето, записите и файловете се запазват.',
    restoreFailed:
      'Не успяхме да потвърдим възстановяването. Опитайте отново, за да проверите същата заявка.',
    restoreIncompatible:
      'Тази версия изисква отделен преглед на данните или източника. Текущата версия не е променена.',
    sharedChecking:
      'Споделено. Другите могат да го отварят, щом приключи автоматичната проверка за сигурност, обикновено до минута.',
    preparing: 'Подготвяне на {app}…',
    preparingUnnamed: 'Подготвяне на това приложение…',
    checkBlocked:
      'Тази версия не премина автоматичната проверка за сигурност, затова не може да се отвори. Помолете в чата да я поправи.',
    checkBlockedSecret:
      'Тази версия съдържа нещо, което прилича на парола или ключ, затова не може да се отвори. Помолете в чата да го премахне.',
    checkBlockedDependency:
      'Тази версия използва пакет с известен проблем със сигурността, затова не може да се отвори. Помолете в чата да го обнови.',
    checkUnavailable:
      'Проверката за сигурност на тази версия не можа да приключи. Опитайте отново след малко.',
  },
  hr: {
    shareTitle: 'Dijeli aplikaciju',
    unshareTitle: 'Prestani dijeliti aplikaciju',
    unshareBody: 'Osobe će izgubiti pristup. Vaša aplikacija i njezini podaci ostaju sačuvani.',
    shareBody: 'Odaberite tko može upotrebljavati ovu aplikaciju i tko je može uređivati.',
    cancel: 'Odustani',
    checking: 'Provjera i spremanje…',
    checkAgain: 'Ponovno provjeri',
    share: 'Dijeli',
    unshare: 'Prestani dijeliti',
    company: 'vaša tvrtka',
    owner: 'vlasnik',
    stateUnavailable: 'Ovo stanje dijeljenja više nije dostupno.',
    checkFailed:
      'Aplikacija nije prošla automatske sigurnosne provjere. Dijeljenje nije promijenjeno.',
    changed:
      'Nismo mogli potvrditi dijeljenje. Ponovno provjerite kako biste ponovno upotrijebili isti zahtjev.',
    privateReadWarning:
      'Ova aplikacija ima rezultate iz izvora: {sources}. Svi s kojima je podijelite mogu ih vidjeti, ali nitko ne može upotrebljavati te veze.',
    unnamedConnection: 'privatna veza',
    versionsLoading: 'Učitavanje spremljenih verzija…',
    versionsFailed:
      'Spremljene verzije nisu dostupne. Ponovno učitajte aplikaciju prije novog pokušaja.',
    version: 'Spremljena verzija {n}',
    current: 'Trenutačna',
    restore: 'Vrati prethodnu verziju',
    restoring: 'Provjera i vraćanje…',
    restoreBody:
      'Time se prethodni kôd i dizajn spremaju kao nova verzija. Dijeljenje, zapisi i datoteke ostaju sačuvani.',
    restoreFailed:
      'Nismo mogli potvrditi vraćanje. Pokušajte ponovno kako biste provjerili isti zahtjev.',
    restoreIncompatible:
      'Ta verzija zahtijeva zaseban pregled podataka ili izvornog kôda. Trenutačna verzija nije promijenjena.',
    sharedChecking:
      'Podijeljeno. Drugi je mogu otvoriti čim završi automatska sigurnosna provjera, obično unutar minute.',
    preparing: 'Priprema aplikacije {app}…',
    preparingUnnamed: 'Priprema ove aplikacije…',
    checkBlocked:
      'Ova verzija nije prošla automatsku sigurnosnu provjeru, pa se ne može otvoriti. Zatražite u chatu da se popravi.',
    checkBlockedSecret:
      'Ova verzija sadrži nešto što izgleda kao lozinka ili ključ, pa se ne može otvoriti. Zatražite u chatu da se ukloni.',
    checkBlockedDependency:
      'Ova verzija koristi paket s poznatim sigurnosnim problemom, pa se ne može otvoriti. Zatražite u chatu da se ažurira.',
    checkUnavailable:
      'Sigurnosnu provjeru ove verzije nije bilo moguće dovršiti. Pokušajte ponovno za trenutak.',
  },
  cs: {
    shareTitle: 'Sdílet aplikaci',
    unshareTitle: 'Přestat sdílet aplikaci',
    unshareBody: 'Lidé ztratí přístup. Vaše aplikace a její data zůstanou zachována.',
    shareBody: 'Vyberte, kdo může tuto aplikaci používat a kdo ji může upravovat.',
    cancel: 'Zrušit',
    checking: 'Kontrola a ukládání…',
    checkAgain: 'Zkontrolovat znovu',
    share: 'Sdílet',
    unshare: 'Přestat sdílet',
    company: 'vaše společnost',
    owner: 'vlastník',
    stateUnavailable: 'Tento stav sdílení už není k dispozici.',
    checkFailed: 'Aplikace neprošla automatickou bezpečnostní kontrolou. Sdílení se nezměnilo.',
    changed: 'Sdílení se nepodařilo potvrdit. Zkontrolujte ho znovu a použijte stejný požadavek.',
    privateReadWarning:
      'Tato aplikace obsahuje výsledky ze zdrojů {sources}. Každý, s kým ji sdílíte, je může zobrazit, ale nikdo tato připojení nemůže používat.',
    unnamedConnection: 'soukromé připojení',
    versionsLoading: 'Načítání uložených verzí…',
    versionsFailed: 'Uložené verze nejsou k dispozici. Před dalším pokusem aplikaci znovu načtěte.',
    version: 'Uložená verze {n}',
    current: 'Aktuální',
    restore: 'Obnovit předchozí verzi',
    restoring: 'Kontrola a obnova…',
    restoreBody:
      'Předchozí kód a návrh se uloží jako nová verze. Sdílení, záznamy a soubory zůstanou zachovány.',
    restoreFailed:
      'Obnovu se nepodařilo potvrdit. Zkuste to znovu a zkontrolujte stejný požadavek.',
    restoreIncompatible:
      'Tato verze vyžaduje samostatnou kontrolu dat nebo zdroje. Aktuální verze se nezměnila.',
    sharedChecking:
      'Sdíleno. Ostatní ji mohou otevřít, jakmile skončí její automatická bezpečnostní kontrola, obvykle do minuty.',
    preparing: 'Příprava aplikace {app}…',
    preparingUnnamed: 'Příprava této aplikace…',
    checkBlocked:
      'Tato verze neprošla automatickou bezpečnostní kontrolou, proto ji nelze otevřít. Požádejte v chatu o opravu.',
    checkBlockedSecret:
      'Tato verze obsahuje něco, co vypadá jako heslo nebo klíč, proto ji nelze otevřít. Požádejte v chatu o odstranění.',
    checkBlockedDependency:
      'Tato verze používá balíček se známým bezpečnostním problémem, proto ji nelze otevřít. Požádejte v chatu o aktualizaci.',
    checkUnavailable:
      'Bezpečnostní kontrolu této verze se nepodařilo dokončit. Zkuste to za chvíli znovu.',
  },
  da: {
    shareTitle: 'Del app',
    unshareTitle: 'Stop deling af app',
    unshareBody: 'Personer mister adgangen. Din app og dens data bevares.',
    shareBody: 'Vælg, hvem der kan bruge denne app, og hvem der kan redigere den.',
    cancel: 'Annuller',
    checking: 'Kontrollerer og gemmer…',
    checkAgain: 'Tjek igen',
    share: 'Del',
    unshare: 'Stop deling',
    company: 'din virksomhed',
    owner: 'ejeren',
    stateUnavailable: 'Denne delingstilstand er ikke længere tilgængelig.',
    checkFailed: 'Appen kunne ikke bestå sine automatiske sikkerhedstjek. Delingen er ikke ændret.',
    changed: 'Vi kunne ikke bekræfte delingen. Tjek igen for at genbruge den samme anmodning.',
    privateReadWarning:
      'Denne app har resultater fra {sources}. Alle, du deler den med, kan se dem, men ingen kan bruge disse forbindelser.',
    unnamedConnection: 'en privat forbindelse',
    versionsLoading: 'Indlæser gemte versioner…',
    versionsFailed: 'Gemte versioner er ikke tilgængelige. Genindlæs appen, før du prøver igen.',
    version: 'Gemt version {n}',
    current: 'Nuværende',
    restore: 'Gendan tidligere version',
    restoring: 'Tjekker og gendanner…',
    restoreBody:
      'Dette gemmer den tidligere kode og det tidligere design som en ny version. Deling, poster og filer bevares.',
    restoreFailed:
      'Vi kunne ikke bekræfte gendannelsen. Prøv igen for at tjekke den samme anmodning.',
    restoreIncompatible:
      'Denne version kræver en separat gennemgang af data eller kilde. Den nuværende version er uændret.',
    sharedChecking:
      'Delt. Andre kan åbne den, så snart dens automatiske sikkerhedstjek er færdigt, normalt inden for et minut.',
    preparing: 'Gør {app} klar…',
    preparingUnnamed: 'Gør denne app klar…',
    checkBlocked:
      'Denne version bestod ikke sit automatiske sikkerhedstjek, så den kan ikke åbnes. Bed i chatten om at rette det.',
    checkBlockedSecret:
      'Denne version indeholder noget, der ligner en adgangskode eller nøgle, så den kan ikke åbnes. Bed i chatten om at fjerne det.',
    checkBlockedDependency:
      'Denne version bruger en pakke med et kendt sikkerhedsproblem, så den kan ikke åbnes. Bed i chatten om at opdatere den.',
    checkUnavailable:
      'Sikkerhedstjekket af denne version kunne ikke gennemføres. Prøv igen om et øjeblik.',
  },
  nl: {
    shareTitle: 'App delen',
    unshareTitle: 'Delen van app stoppen',
    unshareBody:
      'Mensen verliezen de toegang. Je app en de bijbehorende gegevens blijven behouden.',
    shareBody: 'Kies wie deze app mag gebruiken en wie de app mag bewerken.',
    cancel: 'Annuleren',
    checking: 'Controleren en opslaan…',
    checkAgain: 'Opnieuw controleren',
    share: 'Delen',
    unshare: 'Delen ongedaan maken',
    company: 'je bedrijf',
    owner: 'de eigenaar',
    stateUnavailable: 'Deze deelstatus is niet langer beschikbaar.',
    checkFailed:
      'De app heeft de automatische veiligheidscontroles niet doorstaan. De deelinstellingen zijn niet gewijzigd.',
    changed:
      'We konden het delen niet bevestigen. Controleer opnieuw om hetzelfde verzoek opnieuw te gebruiken.',
    privateReadWarning:
      'Deze app bevat resultaten uit {sources}. Iedereen met wie je de app deelt, kan ze zien, maar niemand kan die verbindingen gebruiken.',
    unnamedConnection: 'een privéverbinding',
    versionsLoading: 'Opgeslagen versies worden geladen…',
    versionsFailed:
      'Opgeslagen versies zijn niet beschikbaar. Laad de app opnieuw voordat je het opnieuw probeert.',
    version: 'Opgeslagen versie {n}',
    current: 'Huidig',
    restore: 'Vorige versie herstellen',
    restoring: 'Controleren en herstellen…',
    restoreBody:
      'Hiermee worden de vorige code en het vorige ontwerp als een nieuwe versie opgeslagen. Deelinstellingen, invoer en bestanden blijven behouden.',
    restoreFailed:
      'We konden het herstel niet bevestigen. Probeer het opnieuw om hetzelfde verzoek te controleren.',
    restoreIncompatible:
      'Voor die versie is een afzonderlijke controle van de gegevens of bron nodig. De huidige versie is niet gewijzigd.',
    sharedChecking:
      'Gedeeld. Anderen kunnen hem openen zodra de automatische veiligheidscontrole klaar is, meestal binnen een minuut.',
    preparing: '{app} wordt klaargemaakt…',
    preparingUnnamed: 'Deze app wordt klaargemaakt…',
    checkBlocked:
      'Deze versie heeft de automatische veiligheidscontrole niet doorstaan en kan niet worden geopend. Vraag in de chat om dit te herstellen.',
    checkBlockedSecret:
      'Deze versie bevat iets dat op een wachtwoord of sleutel lijkt en kan niet worden geopend. Vraag in de chat om dit te verwijderen.',
    checkBlockedDependency:
      'Deze versie gebruikt een pakket met een bekend beveiligingsprobleem en kan niet worden geopend. Vraag in de chat om het bij te werken.',
    checkUnavailable:
      'De veiligheidscontrole van deze versie kon niet worden voltooid. Probeer het over een moment opnieuw.',
  },
  en: {
    shareTitle: 'Share App',
    unshareTitle: 'Unshare App',
    unshareBody: 'People will lose access. Your App and its data are kept.',
    shareBody: 'Choose who can use this App and who can edit it.',
    cancel: 'Cancel',
    checking: 'Checking and saving…',
    checkAgain: 'Check again',
    share: 'Share',
    unshare: 'Unshare',
    company: 'your company',
    owner: 'the owner',
    stateUnavailable: 'This sharing state is no longer available.',
    checkFailed: 'The App could not pass its automatic safety checks. Sharing has not changed.',
    changed: 'We couldn’t confirm sharing. Check again to reuse the same request.',
    privateReadWarning:
      'This App has results from {sources}. Everyone you share it with can see them, but nobody can use those connections.',
    unnamedConnection: 'a private connection',
    versionsLoading: 'Loading saved versions…',
    versionsFailed: 'Saved versions are unavailable. Reload the App before trying again.',
    version: 'Saved version {n}',
    current: 'Current',
    restore: 'Restore previous version',
    restoring: 'Checking and restoring…',
    restoreBody:
      'This saves the previous code and design as a new version. Sharing, entries and files are kept.',
    restoreFailed: 'We couldn’t confirm the restore. Try again to check the same request.',
    restoreIncompatible:
      'That version needs a separate data or source review. The current version is unchanged.',
    sharedChecking:
      'Shared. Others can open it as soon as its automatic safety check finishes, usually within a minute.',
    preparing: 'Getting {app} ready…',
    preparingUnnamed: 'Getting this App ready…',
    checkBlocked:
      'This version didn’t pass its automatic safety check, so it can’t be opened. Ask in chat to fix it.',
    checkBlockedSecret:
      'This version contains something that looks like a password or key, so it can’t be opened. Ask in chat to remove it.',
    checkBlockedDependency:
      'This version uses a package with a known security problem, so it can’t be opened. Ask in chat to update it.',
    checkUnavailable: 'This version’s safety check couldn’t finish. Try again in a moment.',
  },
  et: {
    shareTitle: 'Jaga rakendust',
    unshareTitle: 'Lõpeta rakenduse jagamine',
    unshareBody: 'Inimesed kaotavad juurdepääsu. Sinu rakendus ja selle andmed säilivad.',
    shareBody: 'Vali, kes saavad seda rakendust kasutada ja kes seda muuta.',
    cancel: 'Tühista',
    checking: 'Kontrollimine ja salvestamine…',
    checkAgain: 'Kontrolli uuesti',
    share: 'Jaga',
    unshare: 'Lõpeta jagamine',
    company: 'teie ettevõte',
    owner: 'omanik',
    stateUnavailable: 'See jagamisolek pole enam saadaval.',
    checkFailed: 'Rakendus ei läbinud automaatset ohutuskontrolli. Jagamist ei muudetud.',
    changed: 'Me ei saanud jagamist kinnitada. Sama taotluse taaskasutamiseks kontrolli uuesti.',
    privateReadWarning:
      'Sellel rakendusel on allikate {sources} tulemused. Kõik, kellega seda jagad, näevad neid, kuid keegi ei saa neid ühendusi kasutada.',
    unnamedConnection: 'privaatne ühendus',
    versionsLoading: 'Salvestatud versioonide laadimine…',
    versionsFailed:
      'Salvestatud versioonid pole saadaval. Enne uuesti proovimist laadi rakendus uuesti.',
    version: 'Salvestatud versioon {n}',
    current: 'Praegune',
    restore: 'Taasta eelmine versioon',
    restoring: 'Kontrollimine ja taastamine…',
    restoreBody:
      'See salvestab eelmise koodi ja kujunduse uue versioonina. Jagamine, kirjed ja failid säilivad.',
    restoreFailed:
      'Me ei saanud taastamist kinnitada. Sama taotluse kontrollimiseks proovi uuesti.',
    restoreIncompatible:
      'See versioon vajab eraldi andmete või allika ülevaatust. Praegust versiooni ei muudetud.',
    sharedChecking:
      'Jagatud. Teised saavad selle avada kohe, kui automaatne ohutuskontroll on lõppenud, tavaliselt minuti jooksul.',
    preparing: 'Rakenduse {app} ettevalmistamine…',
    preparingUnnamed: 'Selle rakenduse ettevalmistamine…',
    checkBlocked:
      'See versioon ei läbinud automaatset ohutuskontrolli, seega ei saa seda avada. Palu vestluses see parandada.',
    checkBlockedSecret:
      'See versioon sisaldab midagi, mis näeb välja nagu parool või võti, seega ei saa seda avada. Palu vestluses see eemaldada.',
    checkBlockedDependency:
      'See versioon kasutab paketti, millel on teadaolev turvaprobleem, seega ei saa seda avada. Palu vestluses see uuendada.',
    checkUnavailable:
      'Selle versiooni ohutuskontrolli ei saanud lõpetada. Proovi hetke pärast uuesti.',
  },
  fi: {
    shareTitle: 'Jaa sovellus',
    unshareTitle: 'Lopeta sovelluksen jakaminen',
    unshareBody: 'Henkilöt menettävät käyttöoikeuden. Sovellus ja sen tiedot säilytetään.',
    shareBody: 'Valitse, ketkä voivat käyttää tätä sovellusta ja ketkä voivat muokata sitä.',
    cancel: 'Peruuta',
    checking: 'Tarkistetaan ja tallennetaan…',
    checkAgain: 'Tarkista uudelleen',
    share: 'Jaa',
    unshare: 'Lopeta jakaminen',
    company: 'yrityksesi',
    owner: 'omistaja',
    stateUnavailable: 'Tämä jakamisen tila ei ole enää saatavilla.',
    checkFailed:
      'Sovellus ei läpäissyt automaattisia turvallisuustarkistuksia. Jakaminen ei muuttunut.',
    changed: 'Jakamista ei voitu vahvistaa. Tarkista uudelleen, jotta voit käyttää samaa pyyntöä.',
    privateReadWarning:
      'Tämä sovellus sisältää tuloksia lähteistä {sources}. Kaikki, joiden kanssa jaat sen, voivat nähdä ne, mutta kukaan ei voi käyttää näitä yhteyksiä.',
    unnamedConnection: 'yksityinen yhteys',
    versionsLoading: 'Tallennettuja versioita ladataan…',
    versionsFailed:
      'Tallennetut versiot eivät ole saatavilla. Lataa sovellus uudelleen ennen uutta yritystä.',
    version: 'Tallennettu versio {n}',
    current: 'Nykyinen',
    restore: 'Palauta aiempi versio',
    restoring: 'Tarkistetaan ja palautetaan…',
    restoreBody:
      'Aiempi koodi ja ulkoasu tallennetaan uutena versiona. Jakaminen, merkinnät ja tiedostot säilytetään.',
    restoreFailed: 'Palautusta ei voitu vahvistaa. Yritä uudelleen tarkistaaksesi saman pyynnön.',
    restoreIncompatible:
      'Tämä versio edellyttää erillistä data- tai lähdetarkistusta. Nykyinen versio säilyy ennallaan.',
    sharedChecking:
      'Jaettu. Muut voivat avata sen heti, kun automaattinen turvatarkistus on valmis, yleensä minuutissa.',
    preparing: 'Valmistellaan sovellusta {app}…',
    preparingUnnamed: 'Valmistellaan tätä sovellusta…',
    checkBlocked:
      'Tämä versio ei läpäissyt automaattista turvatarkistusta, joten sitä ei voi avata. Pyydä korjausta keskustelussa.',
    checkBlockedSecret:
      'Tämä versio sisältää jotain, mikä näyttää salasanalta tai avaimelta, joten sitä ei voi avata. Pyydä keskustelussa sen poistamista.',
    checkBlockedDependency:
      'Tämä versio käyttää pakettia, jolla on tunnettu tietoturvaongelma, joten sitä ei voi avata. Pyydä keskustelussa päivitystä.',
    checkUnavailable:
      'Tämän version turvatarkistusta ei voitu suorittaa loppuun. Yritä hetken kuluttua uudelleen.',
  },
  fr: {
    shareTitle: 'Partager l’application',
    unshareTitle: 'Ne plus partager l’application',
    unshareBody:
      'Les personnes perdront l’accès. Votre application et ses données sont conservées.',
    shareBody: 'Choisissez qui peut utiliser cette application et qui peut la modifier.',
    cancel: 'Annuler',
    checking: 'Vérification et enregistrement…',
    checkAgain: 'Vérifier à nouveau',
    share: 'Partager',
    unshare: 'Ne plus partager',
    company: 'votre entreprise',
    owner: 'le propriétaire',
    stateUnavailable: 'Cet état de partage n’est plus disponible.',
    checkFailed:
      'L’application n’a pas pu passer ses contrôles de sécurité automatiques. Le partage n’a pas changé.',
    changed:
      'Nous n’avons pas pu confirmer le partage. Vérifiez à nouveau pour réutiliser la même demande.',
    privateReadWarning:
      'Cette application contient des résultats provenant de {sources}. Toutes les personnes avec qui vous la partagez peuvent les voir, mais personne ne peut utiliser ces connexions.',
    unnamedConnection: 'une connexion privée',
    versionsLoading: 'Chargement des versions enregistrées…',
    versionsFailed:
      'Les versions enregistrées ne sont pas disponibles. Rechargez l’application avant de réessayer.',
    version: 'Version enregistrée {n}',
    current: 'Actuelle',
    restore: 'Restaurer la version précédente',
    restoring: 'Vérification et restauration…',
    restoreBody:
      'Le code et le design précédents seront enregistrés comme une nouvelle version. Le partage, les entrées et les fichiers sont conservés.',
    restoreFailed:
      'Nous n’avons pas pu confirmer la restauration. Réessayez pour vérifier la même demande.',
    restoreIncompatible:
      'Cette version nécessite un examen distinct des données ou de la source. La version actuelle n’a pas changé.',
    sharedChecking:
      'Partagée. Les autres pourront l’ouvrir dès la fin de son contrôle de sécurité automatique, généralement en moins d’une minute.',
    preparing: 'Préparation de {app}…',
    preparingUnnamed: 'Préparation de cette application…',
    checkBlocked:
      'Cette version n’a pas passé son contrôle de sécurité automatique : elle ne peut pas être ouverte. Demandez une correction dans le chat.',
    checkBlockedSecret:
      'Cette version contient ce qui ressemble à un mot de passe ou à une clé : elle ne peut pas être ouverte. Demandez de le supprimer dans le chat.',
    checkBlockedDependency:
      'Cette version utilise un paquet présentant une faille de sécurité connue : elle ne peut pas être ouverte. Demandez de le mettre à jour dans le chat.',
    checkUnavailable:
      'Le contrôle de sécurité de cette version n’a pas pu se terminer. Réessayez dans un instant.',
  },
  de: {
    shareTitle: 'App teilen',
    unshareTitle: 'Teilen der App beenden',
    unshareBody: 'Personen verlieren den Zugriff. Ihre App und ihre Daten bleiben erhalten.',
    shareBody: 'Wählen Sie aus, wer diese App verwenden und wer sie bearbeiten kann.',
    cancel: 'Abbrechen',
    checking: 'Wird geprüft und gespeichert…',
    checkAgain: 'Erneut prüfen',
    share: 'Teilen',
    unshare: 'Teilen beenden',
    company: 'Ihr Unternehmen',
    owner: 'die verantwortliche Person',
    stateUnavailable: 'Dieser Freigabestatus ist nicht mehr verfügbar.',
    checkFailed:
      'Die App hat die automatischen Sicherheitsprüfungen nicht bestanden. Die Freigabe wurde nicht geändert.',
    changed:
      'Wir konnten die Freigabe nicht bestätigen. Prüfen Sie erneut, um dieselbe Anfrage wiederzuverwenden.',
    privateReadWarning:
      'Diese App enthält Ergebnisse aus {sources}. Alle Personen, mit denen Sie sie teilen, können diese sehen, aber niemand kann diese Verbindungen verwenden.',
    unnamedConnection: 'eine private Verbindung',
    versionsLoading: 'Gespeicherte Versionen werden geladen…',
    versionsFailed:
      'Gespeicherte Versionen sind nicht verfügbar. Laden Sie die App neu, bevor Sie es erneut versuchen.',
    version: 'Gespeicherte Version {n}',
    current: 'Aktuell',
    restore: 'Vorherige Version wiederherstellen',
    restoring: 'Wird geprüft und wiederhergestellt…',
    restoreBody:
      'Der vorherige Code und das vorherige Design werden als neue Version gespeichert. Freigaben, Einträge und Dateien bleiben erhalten.',
    restoreFailed:
      'Wir konnten die Wiederherstellung nicht bestätigen. Versuchen Sie es erneut, um dieselbe Anfrage zu prüfen.',
    restoreIncompatible:
      'Für diese Version ist eine separate Prüfung der Daten oder Quelle erforderlich. Die aktuelle Version bleibt unverändert.',
    sharedChecking:
      'Geteilt. Andere können sie öffnen, sobald die automatische Sicherheitsprüfung abgeschlossen ist, meist innerhalb einer Minute.',
    preparing: '{app} wird vorbereitet…',
    preparingUnnamed: 'Diese App wird vorbereitet…',
    checkBlocked:
      'Diese Version hat die automatische Sicherheitsprüfung nicht bestanden und kann nicht geöffnet werden. Bitten Sie im Chat um eine Korrektur.',
    checkBlockedSecret:
      'Diese Version enthält etwas, das wie ein Passwort oder Schlüssel aussieht, und kann nicht geöffnet werden. Bitten Sie im Chat, es zu entfernen.',
    checkBlockedDependency:
      'Diese Version nutzt ein Paket mit bekannter Sicherheitslücke und kann nicht geöffnet werden. Bitten Sie im Chat um ein Update.',
    checkUnavailable:
      'Die Sicherheitsprüfung dieser Version konnte nicht abgeschlossen werden. Versuchen Sie es gleich noch einmal.',
  },
  el: {
    shareTitle: 'Κοινή χρήση Εφαρμογής',
    unshareTitle: 'Διακοπή κοινής χρήσης Εφαρμογής',
    unshareBody: 'Τα άτομα θα χάσουν την πρόσβαση. Η Εφαρμογή και τα δεδομένα της διατηρούνται.',
    shareBody:
      'Επιλέξτε ποιοι μπορούν να χρησιμοποιούν αυτή την Εφαρμογή και ποιοι μπορούν να την επεξεργάζονται.',
    cancel: 'Ακύρωση',
    checking: 'Έλεγχος και αποθήκευση…',
    checkAgain: 'Έλεγχος ξανά',
    share: 'Κοινή χρήση',
    unshare: 'Κατάργηση κοινής χρήσης',
    company: 'η εταιρεία σας',
    owner: 'ο κάτοχος',
    stateUnavailable: 'Αυτή η κατάσταση κοινής χρήσης δεν είναι πλέον διαθέσιμη.',
    checkFailed:
      'Η εφαρμογή δεν πέρασε τους αυτόματους ελέγχους ασφαλείας. Η κοινή χρήση δεν άλλαξε.',
    changed:
      'Δεν μπορέσαμε να επιβεβαιώσουμε την κοινή χρήση. Ελέγξτε ξανά για να χρησιμοποιήσετε το ίδιο αίτημα.',
    privateReadWarning:
      'Αυτή η εφαρμογή έχει αποτελέσματα από {sources}. Όλοι όσοι τη μοιράζεστε μπορούν να τα δουν, αλλά κανείς δεν μπορεί να χρησιμοποιήσει αυτές τις συνδέσεις.',
    unnamedConnection: 'μια ιδιωτική σύνδεση',
    versionsLoading: 'Φόρτωση αποθηκευμένων εκδόσεων…',
    versionsFailed:
      'Οι αποθηκευμένες εκδόσεις δεν είναι διαθέσιμες. Επαναφορτώστε την εφαρμογή πριν προσπαθήσετε ξανά.',
    version: 'Αποθηκευμένη έκδοση {n}',
    current: 'Τρέχουσα',
    restore: 'Επαναφορά προηγούμενης έκδοσης',
    restoring: 'Έλεγχος και επαναφορά…',
    restoreBody:
      'Ο προηγούμενος κώδικας και σχεδιασμός αποθηκεύονται ως νέα έκδοση. Η κοινή χρήση, οι καταχωρίσεις και τα αρχεία διατηρούνται.',
    restoreFailed:
      'Δεν μπορέσαμε να επιβεβαιώσουμε την επαναφορά. Προσπαθήστε ξανά για να ελέγξετε το ίδιο αίτημα.',
    restoreIncompatible:
      'Αυτή η έκδοση χρειάζεται ξεχωριστό έλεγχο δεδομένων ή πηγής. Η τρέχουσα έκδοση δεν άλλαξε.',
    sharedChecking:
      'Σε κοινή χρήση. Οι άλλοι μπορούν να την ανοίξουν μόλις ολοκληρωθεί ο αυτόματος έλεγχος ασφαλείας, συνήθως μέσα σε ένα λεπτό.',
    preparing: 'Προετοιμασία του {app}…',
    preparingUnnamed: 'Προετοιμασία αυτής της Εφαρμογής…',
    checkBlocked:
      'Αυτή η έκδοση δεν πέρασε τον αυτόματο έλεγχο ασφαλείας, οπότε δεν μπορεί να ανοιχτεί. Ζητήστε στη συνομιλία να διορθωθεί.',
    checkBlockedSecret:
      'Αυτή η έκδοση περιέχει κάτι που μοιάζει με κωδικό ή κλειδί, οπότε δεν μπορεί να ανοιχτεί. Ζητήστε στη συνομιλία να αφαιρεθεί.',
    checkBlockedDependency:
      'Αυτή η έκδοση χρησιμοποιεί ένα πακέτο με γνωστό πρόβλημα ασφαλείας, οπότε δεν μπορεί να ανοιχτεί. Ζητήστε στη συνομιλία να ενημερωθεί.',
    checkUnavailable:
      'Ο έλεγχος ασφαλείας αυτής της έκδοσης δεν μπόρεσε να ολοκληρωθεί. Δοκιμάστε ξανά σε λίγο.',
  },
  hu: {
    shareTitle: 'Alkalmazás megosztása',
    unshareTitle: 'Alkalmazás megosztásának megszüntetése',
    unshareBody: 'A személyek elveszítik a hozzáférést. Az alkalmazásod és adatai megmaradnak.',
    shareBody: 'Válaszd ki, ki használhatja és ki szerkesztheti ezt az alkalmazást.',
    cancel: 'Mégse',
    checking: 'Ellenőrzés és mentés…',
    checkAgain: 'Újraellenőrzés',
    share: 'Megosztás',
    unshare: 'Megosztás visszavonása',
    company: 'a vállalata',
    owner: 'a tulajdonos',
    stateUnavailable: 'Ez a megosztási állapot már nem érhető el.',
    checkFailed:
      'Az App nem ment át az automatikus biztonsági ellenőrzéseken. A megosztás nem változott.',
    changed:
      'Nem sikerült megerősíteni a megosztást. Ellenőrizze újra, hogy ugyanazt a kérelmet használja fel.',
    privateReadWarning:
      'Ez az App a következő forrásokból származó eredményeket tartalmaz: {sources}. Mindenki, akivel megosztja, láthatja ezeket, de senki sem használhatja ezeket a kapcsolatokat.',
    unnamedConnection: 'egy privát kapcsolat',
    versionsLoading: 'Mentett verziók betöltése…',
    versionsFailed:
      'A mentett verziók nem érhetők el. Töltse újra az Appot, mielőtt újra próbálkozik.',
    version: 'Mentett verzió: {n}',
    current: 'Jelenlegi',
    restore: 'Korábbi verzió visszaállítása',
    restoring: 'Ellenőrzés és visszaállítás…',
    restoreBody:
      'Ez a korábbi kódot és tervet új verzióként menti. A megosztás, a bejegyzések és a fájlok megmaradnak.',
    restoreFailed:
      'Nem sikerült megerősíteni a visszaállítást. Próbálja újra, hogy ellenőrizze ugyanazt a kérelmet.',
    restoreIncompatible:
      'Ehhez a verzióhoz külön adat- vagy forrás-ellenőrzés szükséges. A jelenlegi verzió változatlan maradt.',
    sharedChecking:
      'Megosztva. Mások megnyithatják, amint befejeződik az automatikus biztonsági ellenőrzés, általában egy percen belül.',
    preparing: 'A(z) {app} alkalmazás előkészítése…',
    preparingUnnamed: 'Az alkalmazás előkészítése…',
    checkBlocked:
      'Ez a verzió nem felelt meg az automatikus biztonsági ellenőrzésen, ezért nem nyitható meg. Kérd a javítását a csevegésben.',
    checkBlockedSecret:
      'Ez a verzió jelszónak vagy kulcsnak tűnő adatot tartalmaz, ezért nem nyitható meg. Kérd az eltávolítását a csevegésben.',
    checkBlockedDependency:
      'Ez a verzió ismert biztonsági hibát tartalmazó csomagot használ, ezért nem nyitható meg. Kérd a frissítését a csevegésben.',
    checkUnavailable:
      'Ennek a verziónak a biztonsági ellenőrzését nem sikerült befejezni. Próbáld újra egy pillanat múlva.',
  },
  ga: {
    shareTitle: 'Comhroinn an aip',
    unshareTitle: 'Dí-chomhroinn an aip',
    unshareBody: "Caillfidh daoine rochtain. Coinneofar d'aip agus a sonraí.",
    shareBody: 'Roghnaigh cé atá in ann an aip seo a úsáid agus cé atá in ann é a chur in eagar.',
    cancel: 'Cealaigh',
    checking: 'Á sheiceáil agus á shábháil…',
    checkAgain: 'Seiceáil arís',
    share: 'Comhroinn',
    unshare: 'Dí-chomhroinn',
    company: 'do chuideachta',
    owner: 'an t-úinéir',
    stateUnavailable: 'Níl an staid chomhroinnte seo ar fáil a thuilleadh.',
    checkFailed:
      'Níor éirigh leis an Aip na seiceálacha uathoibríocha sábháilteachta a rith. Níor athraíodh an chomhroinnt.',
    changed:
      'Níorbh fhéidir linn an chomhroinnt a dheimhniú. Seiceáil arís chun an t-iarratas céanna a athúsáid.',
    privateReadWarning:
      'Tá torthaí ón {sources} san Aip seo. Is féidir le gach duine a roinneann tú leo iad a fheiceáil, ach ní féidir le duine ar bith na naisc sin a úsáid.',
    unnamedConnection: 'nasc príobháideach',
    versionsLoading: 'Leaganacha sábháilte á lódáil…',
    versionsFailed:
      'Níl na leaganacha sábháilte ar fáil. Athlódáil an Aip sula ndéanfaidh tú iarracht arís.',
    version: 'Leagan sábháilte {n}',
    current: 'Reatha',
    restore: 'Athchóirigh an leagan roimhe seo',
    restoring: 'Á sheiceáil agus á athchóiriú…',
    restoreBody:
      'Sábhálann sé seo an cód agus an dearadh roimhe seo mar leagan nua. Coinnítear an chomhroinnt, na hiontrálacha agus na comhaid.',
    restoreFailed:
      'Níorbh fhéidir linn an t-athchóiriú a dheimhniú. Bain triail eile as chun an t-iarratas céanna a sheiceáil.',
    restoreIncompatible:
      'Teastaíonn athbhreithniú ar leith ar na sonraí nó ar an bhfoinse ón leagan sin. Níl aon athrú ar an leagan reatha.',
    sharedChecking:
      'Comhroinnte. Is féidir le daoine eile í a oscailt a luaithe a chríochnaíonn a seiceáil sábháilteachta uathoibríoch, laistigh de nóiméad de ghnáth.',
    preparing: 'Aip {app} á hullmhú…',
    preparingUnnamed: 'An aip seo á hullmhú…',
    checkBlocked:
      'Níor éirigh leis an leagan seo sa seiceáil sábháilteachta uathoibríoch, mar sin ní féidir é a oscailt. Iarr sa chomhrá é a dheisiú.',
    checkBlockedSecret:
      'Tá rud éigin sa leagan seo a fhéachann cosúil le pasfhocal nó eochair, mar sin ní féidir é a oscailt. Iarr sa chomhrá é a bhaint.',
    checkBlockedDependency:
      'Úsáideann an leagan seo pacáiste a bhfuil fadhb shlándála aitheanta ann, mar sin ní féidir é a oscailt. Iarr sa chomhrá é a nuashonrú.',
    checkUnavailable:
      'Níorbh fhéidir seiceáil sábháilteachta an leagain seo a chríochnú. Bain triail eile as ar ball beag.',
  },
  it: {
    shareTitle: 'Condividi app',
    unshareTitle: 'Annulla condivisione app',
    unshareBody: 'Le persone perderanno l’accesso. La tua app e i suoi dati verranno conservati.',
    shareBody: 'Scegli chi può usare questa app e chi può modificarla.',
    cancel: 'Annulla',
    checking: 'Verifica e salvataggio…',
    checkAgain: 'Controlla di nuovo',
    share: 'Condividi',
    unshare: 'Annulla condivisione',
    company: 'la tua azienda',
    owner: 'il proprietario',
    stateUnavailable: 'Questo stato di condivisione non è più disponibile.',
    checkFailed:
      "L'app non ha superato i controlli automatici di sicurezza. La condivisione non è cambiata.",
    changed:
      'Non abbiamo potuto confermare la condivisione. Controlla di nuovo per riutilizzare la stessa richiesta.',
    privateReadWarning:
      'Questa app contiene risultati da {sources}. Chiunque la riceva può vederli, ma nessuno può utilizzare queste connessioni.',
    unnamedConnection: 'una connessione privata',
    versionsLoading: 'Caricamento delle versioni salvate…',
    versionsFailed: "Le versioni salvate non sono disponibili. Ricarica l'app prima di riprovare.",
    version: 'Versione salvata {n}',
    current: 'Attuale',
    restore: 'Ripristina la versione precedente',
    restoring: 'Controllo e ripristino…',
    restoreBody:
      'Il codice e il design precedenti verranno salvati come nuova versione. Condivisione, voci e file verranno mantenuti.',
    restoreFailed:
      'Non abbiamo potuto confermare il ripristino. Riprova per controllare la stessa richiesta.',
    restoreIncompatible:
      'Quella versione richiede una revisione separata dei dati o della fonte. La versione attuale non è stata modificata.',
    sharedChecking:
      'Condivisa. Gli altri potranno aprirla appena termina il controllo di sicurezza automatico, di solito entro un minuto.',
    preparing: 'Preparazione di {app}…',
    preparingUnnamed: 'Preparazione di questa app…',
    checkBlocked:
      'Questa versione non ha superato il controllo di sicurezza automatico, quindi non può essere aperta. Chiedi nella chat di correggerla.',
    checkBlockedSecret:
      'Questa versione contiene qualcosa che sembra una password o una chiave, quindi non può essere aperta. Chiedi nella chat di rimuoverla.',
    checkBlockedDependency:
      'Questa versione usa un pacchetto con un problema di sicurezza noto, quindi non può essere aperta. Chiedi nella chat di aggiornarlo.',
    checkUnavailable:
      'Il controllo di sicurezza di questa versione non è stato completato. Riprova tra poco.',
  },
  lv: {
    shareTitle: 'Kopīgot lietotni',
    unshareTitle: 'Pārtraukt lietotnes kopīgošanu',
    unshareBody: 'Personas zaudēs piekļuvi. Jūsu lietotne un tās dati tiks saglabāti.',
    shareBody: 'Izvēlieties, kas var izmantot šo lietotni un kas var to rediģēt.',
    cancel: 'Atcelt',
    checking: 'Notiek pārbaude un saglabāšana…',
    checkAgain: 'Pārbaudīt vēlreiz',
    share: 'Kopīgot',
    unshare: 'Pārtraukt kopīgošanu',
    company: 'jūsu uzņēmums',
    owner: 'īpašnieks',
    stateUnavailable: 'Šis kopīgošanas stāvoklis vairs nav pieejams.',
    checkFailed: 'Lietotne neizturēja automātiskās drošības pārbaudes. Kopīgošana nav mainīta.',
    changed:
      'Nevarējām apstiprināt kopīgošanu. Pārbaudiet vēlreiz, lai atkārtoti izmantotu to pašu pieprasījumu.',
    privateReadWarning:
      'Šai lietotnei ir rezultāti no {sources}. Ikviens, ar ko to kopīgosiet, varēs tos redzēt, taču neviens nevarēs izmantot šos savienojumus.',
    unnamedConnection: 'privāts savienojums',
    versionsLoading: 'Notiek saglabāto versiju ielāde…',
    versionsFailed:
      'Saglabātās versijas nav pieejamas. Pirms mēģināt vēlreiz, atkārtoti ielādējiet lietotni.',
    version: 'Saglabātā versija {n}',
    current: 'Pašreizējā',
    restore: 'Atjaunot iepriekšējo versiju',
    restoring: 'Notiek pārbaude un atjaunošana…',
    restoreBody:
      'Tādējādi iepriekšējais kods un dizains tiek saglabāts kā jauna versija. Kopīgošana, ieraksti un faili tiek saglabāti.',
    restoreFailed:
      'Nevarējām apstiprināt atjaunošanu. Mēģiniet vēlreiz, lai pārbaudītu to pašu pieprasījumu.',
    restoreIncompatible:
      'Šai versijai nepieciešama atsevišķa datu vai avota pārbaude. Pašreizējā versija nav mainīta.',
    sharedChecking:
      'Kopīgots. Citi to var atvērt, tiklīdz beigsies automātiskā drošības pārbaude, parasti minūtes laikā.',
    preparing: 'Notiek lietotnes {app} sagatavošana…',
    preparingUnnamed: 'Notiek šīs lietotnes sagatavošana…',
    checkBlocked:
      'Šī versija neizturēja automātisko drošības pārbaudi, tāpēc to nevar atvērt. Lūdziet tērzēšanā to labot.',
    checkBlockedSecret:
      'Šajā versijā ir kaut kas, kas izskatās pēc paroles vai atslēgas, tāpēc to nevar atvērt. Lūdziet tērzēšanā to noņemt.',
    checkBlockedDependency:
      'Šajā versijā tiek izmantota pakotne ar zināmu drošības problēmu, tāpēc to nevar atvērt. Lūdziet tērzēšanā to atjaunināt.',
    checkUnavailable:
      'Šīs versijas drošības pārbaudi neizdevās pabeigt. Pēc brīža mēģiniet vēlreiz.',
  },
  lt: {
    shareTitle: 'Bendrinti programą',
    unshareTitle: 'Nebebendrinti programos',
    unshareBody: 'Žmonės praras prieigą. Jūsų programa ir jos duomenys bus išsaugoti.',
    shareBody: 'Pasirinkite, kas gali naudoti šią programą ir kas gali ją redaguoti.',
    cancel: 'Atšaukti',
    checking: 'Tikrinama ir išsaugoma…',
    checkAgain: 'Patikrinti dar kartą',
    share: 'Bendrinti',
    unshare: 'Nebebendrinti',
    company: 'jūsų įmonė',
    owner: 'savininkas',
    stateUnavailable: 'Ši bendrinimo būsena nebepasiekiama.',
    checkFailed:
      'Programėlei nepavyko praeiti automatinių saugos patikrų. Bendrinimas nepasikeitė.',
    changed:
      'Nepavyko patvirtinti bendrinimo. Patikrinkite dar kartą, kad galėtumėte pakartotinai naudoti tą pačią užklausą.',
    privateReadWarning:
      'Ši programėlė turi rezultatų iš {sources}. Visi, su kuriais ją bendrinsite, galės juos matyti, bet niekas negalės naudoti šių ryšių.',
    unnamedConnection: 'privatus ryšys',
    versionsLoading: 'Įkeliamos išsaugotos versijos…',
    versionsFailed:
      'Išsaugotos versijos nepasiekiamos. Prieš bandydami dar kartą iš naujo įkelkite programėlę.',
    version: 'Išsaugota versija {n}',
    current: 'Dabartinė',
    restore: 'Atkurti ankstesnę versiją',
    restoring: 'Tikrinama ir atkuriama…',
    restoreBody:
      'Ankstesnis kodas ir dizainas išsaugomi kaip nauja versija. Bendrinimas, įrašai ir failai išsaugomi.',
    restoreFailed:
      'Nepavyko patvirtinti atkūrimo. Bandykite dar kartą, kad patikrintumėte tą pačią užklausą.',
    restoreIncompatible:
      'Šiai versijai reikia atskiros duomenų arba šaltinio peržiūros. Dabartinė versija nepakitusi.',
    sharedChecking:
      'Bendrinama. Kiti gali ją atidaryti, kai tik baigsis automatinė saugos patikra, paprastai per minutę.',
    preparing: 'Ruošiama programa {app}…',
    preparingUnnamed: 'Ruošiama ši programa…',
    checkBlocked:
      'Ši versija neišlaikė automatinės saugos patikros, todėl jos negalima atidaryti. Papraškite pokalbyje ją ištaisyti.',
    checkBlockedSecret:
      'Šioje versijoje yra kažkas, kas atrodo kaip slaptažodis ar raktas, todėl jos negalima atidaryti. Papraškite pokalbyje tai pašalinti.',
    checkBlockedDependency:
      'Šioje versijoje naudojamas paketas su žinoma saugos problema, todėl jos negalima atidaryti. Papraškite pokalbyje jį atnaujinti.',
    checkUnavailable:
      'Šios versijos saugos patikros nepavyko užbaigti. Po akimirkos bandykite dar kartą.',
  },
  mt: {
    shareTitle: 'Aqsam l-App',
    unshareTitle: 'Neħħi l-kondiviżjoni tal-App',
    unshareBody: 'In-nies se jitilfu l-aċċess. L-App u d-dejta tagħha se jinżammu.',
    shareBody: 'Agħżel min jista’ juża din l-App u min jista’ jeditjaha.',
    cancel: 'Ikkanċella',
    checking: 'Qed jiġi vverifikat u salvat…',
    checkAgain: 'Iċċekkja mill-ġdid',
    share: 'Aqsam',
    unshare: 'Neħħi l-qsim',
    company: 'il-kumpanija tiegħek',
    owner: 'is-sid',
    stateUnavailable: 'Dan l-istat tal-qsim m’għadux disponibbli.',
    checkFailed: 'L-App ma għaddietx mill-kontrolli awtomatiċi tas-sigurtà. Il-qsim ma nbidilx.',
    changed:
      'Ma stajniex nikkonfermaw il-qsim. Iċċekkja mill-ġdid biex terġa’ tuża l-istess talba.',
    privateReadWarning:
      'Din l-App għandha riżultati minn {sources}. Kull min taqsamha miegħu jista’ jarahom, iżda ħadd ma jista’ juża dawk il-konnessjonijiet.',
    unnamedConnection: 'konnessjoni privata',
    versionsLoading: 'Qed jitgħabbew il-verżjonijiet salvati…',
    versionsFailed:
      'Il-verżjonijiet salvati mhumiex disponibbli. Erġa’ għabbi l-App qabel terġa’ tipprova.',
    version: 'Verżjoni salvata {n}',
    current: 'Kurrenti',
    restore: 'Irrestawra l-verżjoni preċedenti',
    restoring: 'Qed tiġi ċċekkjata u rrestawrata…',
    restoreBody:
      'Dan isalva l-kodiċi u d-disinn preċedenti bħala verżjoni ġdida. Il-qsim, l-entrati u l-fajls jinżammu.',
    restoreFailed:
      'Ma stajniex nikkonfermaw ir-restawr. Erġa’ pprova biex tiċċekkja l-istess talba.',
    restoreIncompatible:
      'Dik il-verżjoni teħtieġ reviżjoni separata tad-dejta jew tas-sors. Il-verżjoni kurrenti ma nbidlitx.',
    sharedChecking:
      'Maqsuma. L-oħrajn jistgħu jiftħuha hekk kif titlesta l-verifika awtomatika tas-sigurtà, normalment fi ftit minuti.',
    preparing: 'Qed titħejja {app}…',
    preparingUnnamed: 'Qed titħejja din l-app…',
    checkBlocked:
      'Din il-verżjoni ma għaddietx mill-verifika awtomatika tas-sigurtà, għalhekk ma tistax tinfetaħ. Itlob fiċ-chat biex tissewwa.',
    checkBlockedSecret:
      'Din il-verżjoni fiha xi ħaġa li tidher bħal password jew ċavetta, għalhekk ma tistax tinfetaħ. Itlob fiċ-chat biex titneħħa.',
    checkBlockedDependency:
      'Din il-verżjoni tuża pakkett b’problema ta’ sigurtà magħrufa, għalhekk ma tistax tinfetaħ. Itlob fiċ-chat biex jiġi aġġornat.',
    checkUnavailable:
      'Il-verifika tas-sigurtà ta’ din il-verżjoni ma setgħetx titlesta. Erġa’ pprova wara ftit.',
  },
  pl: {
    shareTitle: 'Udostępnij aplikację',
    unshareTitle: 'Przestań udostępniać aplikację',
    unshareBody: 'Osoby utracą dostęp. Twoja aplikacja i jej dane zostaną zachowane.',
    shareBody: 'Wybierz, kto może korzystać z tej aplikacji i kto może ją edytować.',
    cancel: 'Anuluj',
    checking: 'Sprawdzanie i zapisywanie…',
    checkAgain: 'Sprawdź ponownie',
    share: 'Udostępnij',
    unshare: 'Cofnij udostępnienie',
    company: 'Twoja firma',
    owner: 'właściciel',
    stateUnavailable: 'Ten stan udostępniania nie jest już dostępny.',
    checkFailed:
      'Aplikacja nie przeszła automatycznych kontroli bezpieczeństwa. Stan udostępniania się nie zmienił.',
    changed:
      'Nie udało się potwierdzić udostępniania. Sprawdź ponownie, aby użyć tego samego żądania.',
    privateReadWarning:
      'Ta aplikacja zawiera wyniki ze źródeł: {sources}. Każdy, komu ją udostępnisz, będzie mógł je zobaczyć, ale nikt nie będzie mógł używać tych połączeń.',
    unnamedConnection: 'prywatne połączenie',
    versionsLoading: 'Wczytywanie zapisanych wersji…',
    versionsFailed: 'Zapisane wersje są niedostępne. Odśwież aplikację przed ponowną próbą.',
    version: 'Zapisana wersja {n}',
    current: 'Bieżąca',
    restore: 'Przywróć poprzednią wersję',
    restoring: 'Sprawdzanie i przywracanie…',
    restoreBody:
      'Spowoduje to zapisanie poprzedniego kodu i projektu jako nowej wersji. Udostępnianie, wpisy i pliki zostaną zachowane.',
    restoreFailed:
      'Nie udało się potwierdzić przywracania. Spróbuj ponownie, aby sprawdzić to samo żądanie.',
    restoreIncompatible:
      'Ta wersja wymaga osobnego przeglądu danych lub źródła. Bieżąca wersja nie została zmieniona.',
    sharedChecking:
      'Udostępniono. Inni mogą ją otworzyć, gdy tylko zakończy się automatyczna kontrola bezpieczeństwa, zwykle w ciągu minuty.',
    preparing: 'Przygotowywanie aplikacji {app}…',
    preparingUnnamed: 'Przygotowywanie tej aplikacji…',
    checkBlocked:
      'Ta wersja nie przeszła automatycznej kontroli bezpieczeństwa, więc nie można jej otworzyć. Poproś na czacie o naprawę.',
    checkBlockedSecret:
      'Ta wersja zawiera coś, co wygląda na hasło lub klucz, więc nie można jej otworzyć. Poproś na czacie o usunięcie.',
    checkBlockedDependency:
      'Ta wersja używa pakietu ze znaną luką w zabezpieczeniach, więc nie można jej otworzyć. Poproś na czacie o aktualizację.',
    checkUnavailable:
      'Nie udało się ukończyć kontroli bezpieczeństwa tej wersji. Spróbuj ponownie za chwilę.',
  },
  pt: {
    shareTitle: 'Compartilhar aplicativo',
    unshareTitle: 'Deixar de compartilhar aplicativo',
    unshareBody: 'As pessoas perderão o acesso. Seu aplicativo e seus dados serão mantidos.',
    shareBody: 'Escolha quem pode usar este aplicativo e quem pode editá-lo.',
    cancel: 'Cancelar',
    checking: 'Verificando e salvando…',
    checkAgain: 'Verificar novamente',
    share: 'Compartilhar',
    unshare: 'Deixar de compartilhar',
    company: 'sua empresa',
    owner: 'o proprietário',
    stateUnavailable: 'Este estado de compartilhamento não está mais disponível.',
    checkFailed:
      'O aplicativo não passou nas verificações automáticas de segurança. O compartilhamento não foi alterado.',
    changed:
      'Não foi possível confirmar o compartilhamento. Verifique novamente para reutilizar a mesma solicitação.',
    privateReadWarning:
      'Este aplicativo tem resultados de {sources}. Todas as pessoas com quem você compartilhá-lo poderão vê-los, mas ninguém poderá usar essas conexões.',
    unnamedConnection: 'uma conexão privada',
    versionsLoading: 'Carregando versões salvas…',
    versionsFailed:
      'As versões salvas estão indisponíveis. Recarregue o aplicativo antes de tentar novamente.',
    version: 'Versão salva {n}',
    current: 'Atual',
    restore: 'Restaurar versão anterior',
    restoring: 'Verificando e restaurando…',
    restoreBody:
      'Isso salva o código e o design anteriores como uma nova versão. O compartilhamento, as entradas e os arquivos são mantidos.',
    restoreFailed:
      'Não foi possível confirmar a restauração. Tente novamente para verificar a mesma solicitação.',
    restoreIncompatible:
      'Essa versão precisa de uma análise separada dos dados ou da fonte. A versão atual não foi alterada.',
    sharedChecking:
      'Compartilhado. Outras pessoas poderão abri-lo assim que a verificação de segurança automática terminar, normalmente em menos de um minuto.',
    preparing: 'Preparando {app}…',
    preparingUnnamed: 'Preparando este aplicativo…',
    checkBlocked:
      'Esta versão não passou na verificação de segurança automática, então não pode ser aberta. Peça no chat para corrigir.',
    checkBlockedSecret:
      'Esta versão contém algo que parece uma senha ou chave, então não pode ser aberta. Peça no chat para remover.',
    checkBlockedDependency:
      'Esta versão usa um pacote com um problema de segurança conhecido, então não pode ser aberta. Peça no chat para atualizar.',
    checkUnavailable:
      'Não foi possível concluir a verificação de segurança desta versão. Tente novamente em instantes.',
  },
  ro: {
    shareTitle: 'Partajează aplicația',
    unshareTitle: 'Oprește partajarea aplicației',
    unshareBody: 'Persoanele își vor pierde accesul. Aplicația și datele sale sunt păstrate.',
    shareBody: 'Alege cine poate folosi această aplicație și cine o poate edita.',
    cancel: 'Anulează',
    checking: 'Se verifică și se salvează…',
    checkAgain: 'Verifică din nou',
    share: 'Partajează',
    unshare: 'Anulează partajarea',
    company: 'compania ta',
    owner: 'proprietarul',
    stateUnavailable: 'Această stare de partajare nu mai este disponibilă.',
    checkFailed:
      'Aplicația nu a trecut verificările automate de siguranță. Partajarea nu s-a schimbat.',
    changed:
      'Nu am putut confirma partajarea. Verifică din nou pentru a reutiliza aceeași solicitare.',
    privateReadWarning:
      'Această aplicație are rezultate din {sources}. Toți cei cu care o partajezi le pot vedea, dar nimeni nu poate folosi acele conexiuni.',
    unnamedConnection: 'o conexiune privată',
    versionsLoading: 'Se încarcă versiunile salvate…',
    versionsFailed:
      'Versiunile salvate nu sunt disponibile. Reîncarcă aplicația înainte de a încerca din nou.',
    version: 'Versiunea salvată {n}',
    current: 'Actuală',
    restore: 'Restaurează versiunea anterioară',
    restoring: 'Se verifică și se restaurează…',
    restoreBody:
      'Aceasta salvează codul și designul anterior ca versiune nouă. Partajarea, înregistrările și fișierele sunt păstrate.',
    restoreFailed:
      'Nu am putut confirma restaurarea. Încearcă din nou pentru a verifica aceeași solicitare.',
    restoreIncompatible:
      'Acea versiune necesită o verificare separată a datelor sau sursei. Versiunea actuală nu s-a schimbat.',
    sharedChecking:
      'Partajată. Ceilalți o pot deschide imediat ce se termină verificarea automată de siguranță, de obicei în cel mult un minut.',
    preparing: 'Se pregătește {app}…',
    preparingUnnamed: 'Se pregătește această aplicație…',
    checkBlocked:
      'Această versiune nu a trecut de verificarea automată de siguranță, deci nu poate fi deschisă. Cere în chat să fie corectată.',
    checkBlockedSecret:
      'Această versiune conține ceva ce pare o parolă sau o cheie, deci nu poate fi deschisă. Cere în chat să fie eliminată.',
    checkBlockedDependency:
      'Această versiune folosește un pachet cu o problemă de securitate cunoscută, deci nu poate fi deschisă. Cere în chat să fie actualizat.',
    checkUnavailable:
      'Verificarea de siguranță a acestei versiuni nu a putut fi finalizată. Încearcă din nou peste puțin timp.',
  },
  sk: {
    shareTitle: 'Zdieľať aplikáciu',
    unshareTitle: 'Zrušiť zdieľanie aplikácie',
    unshareBody: 'Ľudia stratia prístup. Vaša aplikácia a jej údaje zostanú zachované.',
    shareBody: 'Vyberte, kto môže túto aplikáciu používať a kto ju môže upravovať.',
    cancel: 'Zrušiť',
    checking: 'Kontroluje sa a ukladá…',
    checkAgain: 'Skontrolovať znova',
    share: 'Zdieľať',
    unshare: 'Zrušiť zdieľanie',
    company: 'vaša spoločnosť',
    owner: 'vlastník',
    stateUnavailable: 'Tento stav zdieľania už nie je k dispozícii.',
    checkFailed: 'Aplikácia neprešla automatickou bezpečnostnou kontrolou. Zdieľanie sa nezmenilo.',
    changed:
      'Zdieľanie sa nám nepodarilo potvrdiť. Skontrolujte ho znova a použite rovnakú požiadavku.',
    privateReadWarning:
      'Táto aplikácia obsahuje výsledky zo zdrojov {sources}. Každý, s kým ju zdieľate, ich môže vidieť, no nikto nemôže tieto pripojenia používať.',
    unnamedConnection: 'súkromné pripojenie',
    versionsLoading: 'Načítavajú sa uložené verzie…',
    versionsFailed:
      'Uložené verzie nie sú k dispozícii. Pred ďalším pokusom aplikáciu znova načítajte.',
    version: 'Uložená verzia {n}',
    current: 'Aktuálna',
    restore: 'Obnoviť predchádzajúcu verziu',
    restoring: 'Kontroluje sa a obnovuje…',
    restoreBody:
      'Predchádzajúci kód a návrh sa uložia ako nová verzia. Zdieľanie, záznamy a súbory zostanú zachované.',
    restoreFailed:
      'Obnovenie sa nám nepodarilo potvrdiť. Skúste to znova a skontrolujte rovnakú požiadavku.',
    restoreIncompatible:
      'Táto verzia vyžaduje samostatnú kontrolu údajov alebo zdroja. Aktuálna verzia sa nezmenila.',
    sharedChecking:
      'Zdieľané. Ostatní ju môžu otvoriť, hneď ako sa skončí jej automatická bezpečnostná kontrola, zvyčajne do minúty.',
    preparing: 'Pripravuje sa aplikácia {app}…',
    preparingUnnamed: 'Pripravuje sa táto aplikácia…',
    checkBlocked:
      'Táto verzia neprešla automatickou bezpečnostnou kontrolou, preto ju nemožno otvoriť. Požiadajte v chate o opravu.',
    checkBlockedSecret:
      'Táto verzia obsahuje niečo, čo vyzerá ako heslo alebo kľúč, preto ju nemožno otvoriť. Požiadajte v chate o odstránenie.',
    checkBlockedDependency:
      'Táto verzia používa balík so známym bezpečnostným problémom, preto ju nemožno otvoriť. Požiadajte v chate o aktualizáciu.',
    checkUnavailable:
      'Bezpečnostnú kontrolu tejto verzie sa nepodarilo dokončiť. Skúste to o chvíľu znova.',
  },
  sl: {
    shareTitle: 'Deli aplikacijo',
    unshareTitle: 'Prenehaj deliti aplikacijo',
    unshareBody: 'Ljudje bodo izgubili dostop. Vaša aplikacija in njeni podatki se ohranijo.',
    shareBody: 'Izberite, kdo lahko uporablja to aplikacijo in kdo jo lahko ureja.',
    cancel: 'Prekliči',
    checking: 'Preverjanje in shranjevanje…',
    checkAgain: 'Preveri znova',
    share: 'Deli',
    unshare: 'Prekliči deljenje',
    company: 'vaše podjetje',
    owner: 'lastnik',
    stateUnavailable: 'To stanje deljenja ni več na voljo.',
    checkFailed:
      'Aplikacija ni prestala samodejnih varnostnih preverjanj. Deljenje se ni spremenilo.',
    changed: 'Deljenja ni bilo mogoče potrditi. Preverite znova, če želite uporabiti isto zahtevo.',
    privateReadWarning:
      'Ta aplikacija vsebuje rezultate iz virov {sources}. Vsi, s katerimi jo delite, jih lahko vidijo, vendar nihče ne more uporabljati teh povezav.',
    unnamedConnection: 'zasebna povezava',
    versionsLoading: 'Nalaganje shranjenih različic…',
    versionsFailed:
      'Shranjene različice niso na voljo. Pred ponovnim poskusom znova naložite aplikacijo.',
    version: 'Shranjena različica {n}',
    current: 'Trenutna',
    restore: 'Obnovi prejšnjo različico',
    restoring: 'Preverjanje in obnavljanje…',
    restoreBody:
      'S tem se prejšnja koda in zasnova shranita kot nova različica. Deljenje, vnosi in datoteke se ohranijo.',
    restoreFailed: 'Obnovitve ni bilo mogoče potrditi. Poskusite znova, da preverite isto zahtevo.',
    restoreIncompatible:
      'Ta različica zahteva ločen pregled podatkov ali vira. Trenutna različica se ni spremenila.',
    sharedChecking:
      'V skupni rabi. Drugi jo lahko odprejo, ko se konča samodejno varnostno preverjanje, običajno v minuti.',
    preparing: 'Priprava aplikacije {app}…',
    preparingUnnamed: 'Priprava te aplikacije…',
    checkBlocked:
      'Ta različica ni prestala samodejnega varnostnega preverjanja, zato je ni mogoče odpreti. V klepetu prosite za popravek.',
    checkBlockedSecret:
      'Ta različica vsebuje nekaj, kar je videti kot geslo ali ključ, zato je ni mogoče odpreti. V klepetu prosite za odstranitev.',
    checkBlockedDependency:
      'Ta različica uporablja paket z znano varnostno težavo, zato je ni mogoče odpreti. V klepetu prosite za posodobitev.',
    checkUnavailable:
      'Varnostnega preverjanja te različice ni bilo mogoče dokončati. Poskusite znova čez trenutek.',
  },
  es: {
    shareTitle: 'Compartir aplicación',
    unshareTitle: 'Dejar de compartir la aplicación',
    unshareBody: 'Las personas perderán el acceso. La aplicación y sus datos se conservarán.',
    shareBody: 'Elige quién puede usar esta aplicación y quién puede editarla.',
    cancel: 'Cancelar',
    checking: 'Comprobando y guardando…',
    checkAgain: 'Comprobar de nuevo',
    share: 'Compartir',
    unshare: 'Dejar de compartir',
    company: 'tu empresa',
    owner: 'el propietario',
    stateUnavailable: 'Este estado de uso compartido ya no está disponible.',
    checkFailed:
      'La aplicación no superó las comprobaciones automáticas de seguridad. El uso compartido no ha cambiado.',
    changed:
      'No pudimos confirmar el uso compartido. Compruébalo de nuevo para reutilizar la misma solicitud.',
    privateReadWarning:
      'Esta aplicación tiene resultados de {sources}. Todos aquellos con quienes la compartas podrán verlos, pero nadie podrá usar esas conexiones.',
    unnamedConnection: 'una conexión privada',
    versionsLoading: 'Cargando versiones guardadas…',
    versionsFailed:
      'Las versiones guardadas no están disponibles. Vuelve a cargar la aplicación antes de intentarlo de nuevo.',
    version: 'Versión guardada {n}',
    current: 'Actual',
    restore: 'Restaurar versión anterior',
    restoring: 'Comprobando y restaurando…',
    restoreBody:
      'Esto guarda el código y el diseño anteriores como una versión nueva. Se conservan el uso compartido, las entradas y los archivos.',
    restoreFailed:
      'No pudimos confirmar la restauración. Inténtalo de nuevo para comprobar la misma solicitud.',
    restoreIncompatible:
      'Esa versión necesita una revisión independiente de los datos o del origen. La versión actual no ha cambiado.',
    sharedChecking:
      'Compartida. Los demás podrán abrirla en cuanto termine su comprobación de seguridad automática, normalmente en un minuto.',
    preparing: 'Preparando {app}…',
    preparingUnnamed: 'Preparando esta app…',
    checkBlocked:
      'Esta versión no superó su comprobación de seguridad automática, así que no se puede abrir. Pide en el chat que se corrija.',
    checkBlockedSecret:
      'Esta versión contiene algo que parece una contraseña o clave, así que no se puede abrir. Pide en el chat que se elimine.',
    checkBlockedDependency:
      'Esta versión usa un paquete con un problema de seguridad conocido, así que no se puede abrir. Pide en el chat que se actualice.',
    checkUnavailable:
      'No se pudo completar la comprobación de seguridad de esta versión. Inténtalo de nuevo en un momento.',
  },
  sv: {
    shareTitle: 'Dela app',
    unshareTitle: 'Sluta dela app',
    unshareBody: 'Andra förlorar åtkomsten. Din app och dess data finns kvar.',
    shareBody: 'Välj vem som kan använda appen och vem som kan redigera den.',
    cancel: 'Avbryt',
    checking: 'Kontrollerar och sparar…',
    checkAgain: 'Kontrollera igen',
    share: 'Dela',
    unshare: 'Sluta dela',
    company: 'ditt företag',
    owner: 'ägaren',
    stateUnavailable: 'Delningsläget är inte längre tillgängligt.',
    checkFailed:
      'Appen klarade inte de automatiska säkerhetskontrollerna. Delningen har inte ändrats.',
    changed: 'Vi kunde inte bekräfta delningen. Kontrollera igen för att använda samma begäran.',
    privateReadWarning:
      'Den här appen har resultat från {sources}. Alla du delar den med kan se dem, men ingen kan använda de anslutningarna.',
    unnamedConnection: 'en privat anslutning',
    versionsLoading: 'Läser in sparade versioner…',
    versionsFailed:
      'Sparade versioner är inte tillgängliga. Läs in appen igen innan du försöker på nytt.',
    version: 'Sparad version {n}',
    current: 'Aktuell',
    restore: 'Återställ tidigare version',
    restoring: 'Kontrollerar och återställer…',
    restoreBody:
      'Den tidigare koden och designen sparas som en ny version. Delning, poster och filer behålls.',
    restoreFailed:
      'Vi kunde inte bekräfta återställningen. Försök igen för att kontrollera samma begäran.',
    restoreIncompatible:
      'Den versionen behöver en separat granskning av data eller källa. Den aktuella versionen är oförändrad.',
    sharedChecking:
      'Delad. Andra kan öppna den så snart den automatiska säkerhetskontrollen är klar, oftast inom en minut.',
    preparing: 'Gör {app} redo…',
    preparingUnnamed: 'Gör appen redo…',
    checkBlocked:
      'Den här versionen klarade inte den automatiska säkerhetskontrollen och kan inte öppnas. Be i chatten om att få det åtgärdat.',
    checkBlockedSecret:
      'Den här versionen innehåller något som ser ut som ett lösenord eller en nyckel och kan inte öppnas. Be i chatten om att ta bort det.',
    checkBlockedDependency:
      'Den här versionen använder ett paket med ett känt säkerhetsproblem och kan inte öppnas. Be i chatten om att uppdatera det.',
    checkUnavailable:
      'Säkerhetskontrollen för den här versionen kunde inte slutföras. Försök igen om en stund.',
  },
};
export default copy;
